import { createFileRoute, Link, notFound, useParams } from "@tanstack/react-router";
import { ArrowLeft, ArrowUp, BookOpen } from "lucide-react";
import { useEffect, useState } from "react";
import { RatingStars } from "@/components/RatingStars";

import { ShareButtons } from "@/components/ShareButtons";
import { RelatedArticles } from "@/components/RelatedArticles";
import { RelatedTools } from "@/components/RelatedTools";
import { getRelatedArticlesForArticle, getRelatedToolsForArticle } from "@/lib/related";
import { InfinitePayBanner } from "@/components/InfinitePayBanner";
import { ArticleMeiMasterBanner } from "@/components/ArticleMeiMasterBanner";
import portalImg from "@/assets/artigo-portal-mei.webp";
import oqueImg from "@/assets/artigo-o-que-e-mei.webp";
import formImg from "@/assets/artigo-formalizacao.webp";
import dasImg from "@/assets/artigo-das-boleto.webp";
import funcImg from "@/assets/artigo-mei-funcionario.webp";
import meiMeImg from "@/assets/artigo-mei-x-me.webp";
import parcImg from "@/assets/artigo-parcelamento-mei.webp";
import limiteImg from "@/assets/artigo-limite-faturamento.webp";
import cancelImg from "@/assets/artigo-cancelar-mei.webp";
import atvImg from "@/assets/artigo-atividades-mei.webp";
import alvaraImg from "@/assets/artigo-alvara-mei.webp";
import alterarImg from "@/assets/artigo-alterar-dados-mei.webp";
import nfImg from "@/assets/artigo-nota-fiscal-mei.webp";
import nomeSujoImg from "@/assets/artigo-mei-nome-sujo.webp";
import reformaImg from "@/assets/artigo-reforma-tributaria-mei.webp";
import inssImg from "@/assets/artigo-mei-inss.webp";
import contaPjImg from "@/assets/artigo-conta-pj-mei.webp";
import irpfImg from "@/assets/artigo-mei-irpf.webp";
import beneficiosImg from "@/assets/artigo-beneficios-mei.webp";
import certDigImg from "@/assets/artigo-certificado-digital-mei.webp";
import migrarMeImg from "@/assets/artigo-migrar-mei-me.webp";
import ecommerceImg from "@/assets/artigo-mei-ecommerce.webp";
import simplesNacImg from "@/assets/artigo-mei-simples-nacional.webp";
import femininoImg from "@/assets/artigo-mei-feminino.webp";
import inadimplenteImg from "@/assets/artigo-mei-inadimplente.webp";
import servicoPjImg from "@/assets/artigo-mei-prestar-servico-pj.webp";
import cltImg from "@/assets/artigo-mei-clt.webp";
import socioImg from "@/assets/artigo-mei-socio.webp";
import cnpjRapidoImg from "@/assets/artigo-cartao-cnpj-rapido.webp";
import cnpjVantagensImg from "@/assets/artigo-vantagens-cartao-cnpj.webp";
import cnpjPassoImg from "@/assets/artigo-passo-cartao-cnpj.webp";
import cnpjDocImg from "@/assets/artigo-documentacao-cnpj.webp";
import cnpjLegalImg from "@/assets/artigo-importancia-cnpj.webp";
import nfeEletronicaImg from "@/assets/artigo-mei-nfe-eletronica.webp";
import inss2026Img from "@/assets/artigo-inss-mei-2026.webp";
import obrigMensaisImg from "@/assets/artigo-obrigacoes-mensais-mei.webp";
import desenquadramentoImg from "@/assets/artigo-desenquadramento-mei.png";

import emprestimoMeiImg from "@/assets/artigo-simulador-emprestimo-mei.jpg";
import maquininhaImg from "@/assets/artigo-simulador-maquininha.jpg";
import meiVsCltImg from "@/assets/artigo-mei-vs-clt-2026.jpg";
import iaCnaeMeiImg from "@/assets/artigo-ia-cnae-mei-2026.jpg";
import dasnSimeiImg from "@/assets/artigo-dasn-simei-declaracao-anual.jpg";
import entregadorMlImg from "@/assets/artigo-entregador-mercado-livre-mei.webp";
import mei2026CgsnImg from "@/assets/artigo-mei-2026-cgsn-183.jpg";
import ferramentasKitImg from "@/assets/artigo-ferramentas-mei-kit.png";
import dasMei2026Img from "@/assets/artigo-das-mei-2026.jpg";
import irpfMei2026Img from "@/assets/artigo-mei-irpf.webp";
import gestaoFinanceiraImg from "@/assets/artigo-conta-pj-mei.webp";
import creditoEmprestimoImg from "@/assets/artigo-simulador-emprestimo-mei.jpg";
import emprestimoCaixaImg from "@/assets/artigo-emprestimo-caixa-mei.jpg";

import marketingDigitalImg from "@/assets/artigo-mei-ecommerce.webp";
import nanoempreendedorImg from "@/assets/artigo-reforma-tributaria-mei.webp";

type ArtigoMeta = {
  titulo: string;
  resumo: string;
  categoria: string;
  tempo: string;
  imagem: string;
  tag: string;
  metaTitle?: string;
  metaDescription?: string;
};

type ArtigoData = ArtigoMeta & { html: string };

const ARTIGOS: Record<string, ArtigoMeta> = {
  "portal-mei-guia-completo": {
    titulo: "Portal MEI: como acessar, usar e não se perder nos serviços do governo",
    resumo:
      "Guia completo dos portais oficiais do MEI: Portal do Empreendedor, PGMEI, Gov.br, app MEI e DASN-SIMEI.",
    metaDescription:
      "Guia completo do Portal do Empreendedor e portais oficiais do MEI. Aprenda a acessar o PGMEI, emitir o DAS, declarar o DASN-SIMEI e regularizar sua empresa.",
    categoria: "Guia Completo",
    tempo: "12 min de leitura",
    imagem: portalImg,
    tag: "Portais oficiais do governo",
    metaTitle: "Portal MEI: como acessar e usar (guia 2026)",
  },
  "o-que-e-mei": {
    titulo: "O que é MEI? Entenda tudo sobre o Microempreendedor Individual",
    resumo: "Benefícios, obrigações, impostos, INSS, nota fiscal e perfis que se encaixam no MEI.",
    metaDescription:
      "Quer entender o que é MEI em 2026? Descubra o que significa ser Microempreendedor Individual, quais os benefícios do INSS, impostos, obrigações e regras.",
    categoria: "Fundamentos",
    tempo: "10 min de leitura",
    imagem: oqueImg,
    tag: "Conceitos básicos do MEI",
    metaTitle: "O que é MEI? Guia completo do Microempreendedor",
  },
  "como-formalizar-mei": {
    titulo: "Como formalizar seu MEI: passo a passo completo",
    resumo:
      "Critérios, atividades permitidas, abertura no Portal do Empreendedor e o que fazer logo depois.",
    metaDescription:
      "Veja como formalizar MEI em 2026 com nosso passo a passo completo: confira os critérios de abertura, documentos necessários, CNAEs permitidos e tire o CNPJ.",
    categoria: "Formalização",
    tempo: "9 min de leitura",
    imagem: formImg,
    tag: "Abertura do CNPJ",
  },
  "das-boleto-mei": {
    titulo: "DAS do MEI: como gerar, pagar e parcelar o boleto mensal",
    resumo:
      "Aprenda como emitir o DAS MEI 2026, gerar a segunda via do boleto, pagar por PIX, consultar todos os débitos e parcelar impostos atrasados de forma simples.",
    metaDescription:
      "Aprenda como emitir o DAS MEI 2026, gerar a segunda via do boleto, pagar por PIX, consultar todos os débitos e parcelar impostos atrasados de forma simples.",
    categoria: "Tributos",
    tempo: "11 min de leitura",
    imagem: dasImg,
    tag: "Boleto mensal do MEI",
    metaTitle: "DAS MEI 2026: Emitir Boleto, Pagar por PIX e Parcelar",
  },
  "como-calcular-das-mei-2026": {
    titulo: "Como Calcular o DAS MEI 2026: Guia Completo com Tabela Atualizada",
    resumo:
      "Tabela completa do DAS MEI 2026, fórmula de cálculo, diferença entre comércio/serviço/caminhoneiro, exemplos práticos e passo a passo para emitir no PGMEI.",
    metaDescription:
      "Descubra quanto custa o DAS MEI em 2026, veja a tabela atualizada, exemplos práticos e calcule sua guia gratuitamente com a Calculadora MEI Toolbox.",
    categoria: "Tributos",
    tempo: "14 min de leitura",
    imagem: dasMei2026Img,
    tag: "Guia MEI 2026",
    metaTitle: "Como Calcular o DAS MEI 2026: Tabela e Calculadora",
  },
  "mei-pode-ter-funcionario": {
    titulo: "MEI pode ter funcionário? Regras, custos e como contratar em 2025",
    resumo:
      "MEI pode ter 1 funcionário. Veja salário mínimo, custos com FGTS e INSS, eSocial, demissão e regras para contratar outro MEI.",
    categoria: "Trabalhista",
    tempo: "13 min de leitura",
    imagem: funcImg,
    tag: "Contratação de empregado pelo MEI",
    metaTitle: "MEI pode ter funcionário? Custos, eSocial e regras (2025)",
    metaDescription:
      "MEI pode contratar 1 funcionário em 2026. Veja salário mínimo, FGTS, INSS, eSocial, custos reais, processo de demissão e regras para contratar outro MEI.",
  },
  "mei-x-me-diferencas": {
    titulo: "MEI x ME: qual é o melhor para o seu negócio? Entenda as diferenças",
    resumo:
      "Limites, impostos, sócios, funcionários e quando faz sentido migrar do MEI para a Microempresa.",
    metaDescription:
      "Entenda as principais diferenças entre MEI e ME in 2026: compare limites de faturamento, impostos, funcionários, regras de sócios e saiba quando vale migrar.",
    categoria: "MEI x ME",
    tempo: "13 min de leitura",
    imagem: meiMeImg,
    tag: "Comparativo MEI vs ME",
    metaTitle: "MEI x ME: qual é melhor? Diferenças e quando migrar",
  },
  "parcelamento-dividas-mei": {
    titulo: "Parcelamento de dívidas do MEI: como regularizar a situação",
    resumo:
      "Como parcelar DAS em atraso, programas especiais e como recuperar todos os direitos do MEI.",
    metaDescription:
      "Veja como solicitar o parcelamento de dívidas do MEI em 2026: regularize o DAS em atraso pelo Simples Nacional, evite a dívida ativa e recupere seus direitos.",
    categoria: "Regularização",
    tempo: "13 min de leitura",
    imagem: parcImg,
    tag: "DAS em atraso",
  },
  "limite-faturamento-mei": {
    titulo: "Limite de Faturamento MEI 2026: Quanto Você Pode Ganhar Sem Perder o MEI",
    resumo:
      "Tudo sobre o teto de faturamento do MEI em 2026: limite proporcional, PLC 108/2021, o que acontece se ultrapassar até ou acima de 20% e como planejar a migração.",
    metaDescription:
      "Descubra o limite de faturamento MEI em 2026. Veja o cálculo do limite proporcional, a proposta do PLC 108/2021 e o que fazer ao ultrapassar o teto anual do MEI.",
    categoria: "Faturamento",
    tempo: "15 min de leitura",
    imagem: limiteImg,
    tag: "Teto do MEI",
    metaTitle: "Limite de Faturamento MEI 2026: Quanto Você Pode Ganhar",
  },
  "como-cancelar-mei-dar-baixa": {
    titulo: "Como cancelar o MEI: passo a passo para dar baixa no CNPJ",
    resumo:
      "Quando cancelar, o que regularizar antes, passo a passo no Portal do Empreendedor e o que muda no INSS.",
    metaDescription:
      "Passo a passo completo para cancelar o MEI e dar baixa no CNPJ em 2026. Veja o que regularizar antes, como declarar a DASN de extinção e o impacto no INSS.",
    categoria: "Cancelamento",
    tempo: "13 min de leitura",
    imagem: cancelImg,
    tag: "Baixa do CNPJ",
  },
  "atividades-permitidas-mei": {
    titulo: "Atividades permitidas para MEI: lista e como escolher a sua",
    resumo:
      "Mais de 600 atividades autorizadas, CNAEs, profissões não permitidas e como verificar a sua.",
    metaDescription:
      "Lista completa de atividades permitidas para MEI em 2026: consulte os CNAEs autorizados, ocupações de comércio, serviços e quais profissões são proibidas.",
    categoria: "Atividades",
    tempo: "13 min de leitura",
    imagem: atvImg,
    tag: "Lista de CNAEs",
  },
  "alvara-funcionamento-mei": {
    titulo: "Alvará de funcionamento para MEI: quando é obrigatório e como obter",
    resumo:
      "Quando o MEI precisa de alvará, como solicitar pela prefeitura e quais outras licenças podem ser exigidas.",
    metaDescription:
      "Saiba quando o alvará de funcionamento do MEI é obrigatório em 2026, como solicitar a dispensa ou emissão pela prefeitura e quais licenças podem ser exigidas.",
    categoria: "Licenças",
    tempo: "10 min de leitura",
    imagem: alvaraImg,
    tag: "Alvará e licenças",
    metaTitle: "Alvará MEI: quando é obrigatório e como obter",
  },
  "alterar-dados-mei": {
    titulo: "Como alterar dados do MEI: nome fantasia, endereço e atividade",
    resumo:
      "Passo a passo gratuito no Portal do Empreendedor para atualizar nome, endereço e CNAEs.",
    metaDescription:
      "Saiba como alterar dados do MEI em 2026 de forma gratuita no Portal do Empreendedor: altere nome fantasia, endereço comercial, telefone e as atividades CNAE.",
    categoria: "Cadastro",
    tempo: "9 min de leitura",
    imagem: alterarImg,
    tag: "Atualização cadastral",
    metaTitle: "Como alterar dados do MEI: nome, endereço e CNAE",
  },
  "nota-fiscal-mei": {
    titulo: "Como emitir nota fiscal pelo MEI: guia completo",
    resumo:
      "Quando emitir, tipos de nota (NFS-e, NF-e, NFC-e) e como se cadastrar pela prefeitura ou SEFAZ.",
    metaDescription:
      "Veja como emitir nota fiscal MEI em 2026: entenda os tipos (NFS-e, NF-e, NFC-e), regras de obrigatoriedade, cadastro na prefeitura ou SEFAZ e passo a passo.",
    categoria: "Nota Fiscal",
    tempo: "10 min de leitura",
    imagem: nfImg,
    tag: "Emissão de NF",
  },
  "mei-nome-sujo": {
    titulo: "MEI com nome sujo: é possível abrir MEI com restrição no CPF?",
    resumo:
      "Sim, dá para abrir MEI mesmo negativado. Veja as limitações práticas e como se preparar.",
    metaDescription:
      "Está com MEI com nome sujo? Descubra se é possível abrir CNPJ com restrição no CPF em 2026, quais as limitações de crédito e como regularizar sua situação.",
    categoria: "Crédito",
    tempo: "9 min de leitura",
    imagem: nomeSujoImg,
    tag: "Restrição no CPF",
    metaTitle: "MEI com nome sujo: é possível abrir? (2026)",
  },
  "reforma-tributaria-mei": {
    titulo: "MEI e a Reforma Tributária: o que muda para o microempreendedor",
    resumo:
      "CBS, IBS, Imposto Seletivo e a transição até 2033 — entenda o que afeta (e o que não afeta) o seu MEI.",
    metaDescription:
      "Saiba o que muda para o MEI com a Reforma Tributária em 2026. Entenda os impactos do CBS, IBS e da transição até 2033 na rotina e nos impostos do seu negócio.",
    categoria: "Reforma Tributária",
    tempo: "10 min de leitura",
    imagem: reformaImg,
    tag: "Mudanças até 2033",
    metaTitle: "Reforma Tributária: o que muda para o MEI",
  },
  "mei-inss-aposentadoria": {
    titulo: "MEI e INSS: como funciona a aposentadoria do microempreendedor",
    resumo:
      "Quanto o MEI contribui, quais benefícios tem e como complementar para se aposentar with mais que um salário mínimo.",
    metaDescription:
      "Entenda a aposentadoria e o INSS do MEI: quanto custa a contribuição mensal no DAS, quais os direitos previdenciários e como complementar para receber mais.",
    categoria: "INSS",
    tempo: "10 min de leitura",
    imagem: inssImg,
    tag: "Aposentadoria do MEI",
    metaTitle: "MEI e INSS: como funciona a aposentadoria",
  },
  "conta-pj-mei": {
    titulo: "MEI precisa de conta bancária PJ? Regras e vantagens",
    resumo:
      "Não é obrigatório por lei, mas separar as finanças do negócio é uma das melhores decisões do MEI.",
    metaDescription:
      "Entenda por que abrir uma conta PJ MEI é fundamental em 2026: veja se é obrigatório por lei, as vantagens para o negócio e os melhores bancos digitais grátis.",
    categoria: "Conta PJ",
    tempo: "9 min de leitura",
    imagem: contaPjImg,
    tag: "Conta bancária do MEI",
  },
  "mei-imposto-de-renda": {
    titulo: "MEI precisa declarar Imposto de Renda? Entenda as regras",
    resumo:
      "DASN-SIMEI é uma coisa, IRPF é outra. Veja quando o MEI precisa fazer a declaração de pessoa física.",
    metaDescription:
      "Saiba quando o MEI precisa declarar o Imposto de Renda Pessoa Física (IRPF). Aprenda a calcular a parcela isenta, o rendimento tributável e evite multas.",
    categoria: "Imposto de Renda",
    tempo: "10 min de leitura",
    imagem: irpfImg,
    tag: "IRPF do MEI",
  },
  "beneficios-previdenciarios-mei": {
    titulo: "Benefícios previdenciários do MEI: auxílio-doença, salário-maternidade e pensão",
    resumo: "Pagar o DAS dá acesso a uma rede de proteção que vai muito além da aposentadoria.",
    metaDescription:
      "Conheça todos os benefícios previdenciários do MEI em 2026: saiba como ter direito a aposentadoria, auxílio-doença, salário-maternidade e pensão pelo INSS.",
    categoria: "Benefícios INSS",
    tempo: "10 min de leitura",
    imagem: beneficiosImg,
    tag: "Auxílios e pensões",
    metaTitle: "Benefícios do INSS para MEI: auxílios e pensão",
  },
  "certificado-digital-mei": {
    titulo: "Certificado digital para MEI: quando é obrigatório e como obter",
    resumo: "e-CPF ou e-CNPJ? Veja em quais situações é exigido, qual escolher e quanto custa.",
    categoria: "Certificado Digital",
    tempo: "10 min de leitura",
    imagem: certDigImg,
    tag: "e-CNPJ e e-CPF",
    metaTitle: "Certificado digital MEI: é obrigatório? Quanto custa? (2025)",
    metaDescription:
      "MEI precisa de certificado digital? Veja quando o e-CNPJ é obrigatório em 2026, qual modelo escolher (A1 ou A3), preços médios e como obter passo a passo.",
  },
  "migrar-mei-para-me": {
    titulo: "Como migrar do MEI para ME: quando e como fazer a transição",
    resumo:
      "Chegou no teto ou quer crescer? Veja a transição para Microempresa sem perder histórico.",
    categoria: "Migração",
    tempo: "10 min de leitura",
    imagem: migrarMeImg,
    tag: "MEI → ME",
    metaTitle: "Migrar do MEI para ME: passo a passo da transição em 2025",
    metaDescription:
      "Ultrapassou R$ 81 mil de faturamento? Veja como migrar do MEI para Microempresa em 2026: custos, Junta Comercial, Simples Nacional e o que fazer com seu CNPJ.",
  },
  "mei-e-commerce": {
    titulo: "MEI e e-commerce: tudo que você precisa para vender online",
    resumo: "Marketplaces, loja própria, NF, limite de faturamento e dropshipping para o MEI.",
    categoria: "E-commerce",
    tempo: "10 min de leitura",
    imagem: ecommerceImg,
    tag: "Vendas online",
    metaTitle: "MEI pode vender online? Guia completo de e-commerce (2025)",
    metaDescription:
      "MEI pode ter loja virtual e vender em marketplaces como Mercado Livre, Shopee e Shopify. Veja regras de NF, inscrição estadual, frete e dropshipping em 2026.",
  },
  "mei-simples-nacional": {
    titulo: "MEI e o Simples Nacional: como funciona a tributação do microempreendedor",
    resumo: "SIMEI, DAS fixo e isenções: por que é o regime tributário mais favorável do Brasil.",
    categoria: "Tributação",
    tempo: "10 min de leitura",
    imagem: simplesNacImg,
    tag: "SIMEI",
    metaTitle: "MEI e Simples Nacional: como funciona o SIMEI (2025)",
    metaDescription:
      "Entenda o SIMEI, o DAS fixo do MEI, as isenções de IRPJ, PIS e COFINS e por que esse é o regime tributário mais favorável do Brasil. Com exemplos e cálculos.",
  },
  "mei-feminino": {
    titulo: "MEI feminino: direitos, benefícios e proteções para a mulher empreendedora",
    resumo: "Salário-maternidade, auxílio-doença, aposentadoria e crédito para a mulher MEI.",
    categoria: "Mulher Empreendedora",
    tempo: "10 min de leitura",
    imagem: femininoImg,
    tag: "Direitos da mulher MEI",
    metaTitle: "MEI feminino: salário-maternidade e direitos da mulher MEI",
    metaDescription:
      "Mulher MEI tem direito a salário-maternidade, auxílio-doença, aposentadoria e linhas de crédito específicas em 2026. Veja regras, carências e como solicitar.",
  },
  "mei-inadimplente": {
    titulo: "MEI inadimplente: o que acontece se você parar de pagar o DAS",
    resumo: "Multas, juros, perda de INSS, dívida ativa e como regularizar a situação.",
    categoria: "Inadimplência",
    tempo: "10 min de leitura",
    imagem: inadimplenteImg,
    tag: "DAS em atraso",
    metaTitle: "MEI inadimplente: consequências e como regularizar (2025)",
    metaDescription:
      "Parou de pagar o DAS do MEI? Veja multas e juros, risco de cancelamento do CNPJ, perda de benefícios do INSS e como regularizar com parcelamento ou REFIS.",
  },
  "mei-prestar-servico-pj": {
    titulo: "MEI pode prestar serviço para empresa (PJ)? Saiba como funciona",
    resumo: "É legal sim. Veja como contratar com PJ e evitar a pejotização ilegal.",
    categoria: "Serviços para PJ",
    tempo: "10 min de leitura",
    imagem: servicoPjImg,
    tag: "Contratos com empresas",
    metaTitle: "MEI pode prestar serviço para PJ? Pejotização e cuidados",
    metaDescription:
      "MEI pode emitir nota fiscal e prestar serviço para empresa em 2026. Veja como evitar pejotização, retenção de impostos e como se proteger juridicamente.",
  },
  "mei-clt-carteira-assinada": {
    titulo: "MEI pode ter emprego com carteira assinada ao mesmo tempo?",
    resumo: "Dá para ser MEI e CLT. Veja regras, INSS e cuidados antes de abrir o CNPJ.",
    categoria: "MEI + CLT",
    tempo: "9 min de leitura",
    imagem: cltImg,
    tag: "Combinação MEI + CLT",
    metaTitle: "MEI pode ter carteira assinada? Regras de MEI + CLT (2025)",
    metaDescription:
      "Sim, ser MEI e CLT é compatível em 2026. Veja cláusula de exclusividade, regras de INSS e IR, casos de servidor público e militar e checklist antes de abrir.",
  },
  "mei-pode-ter-socio": {
    titulo: "MEI pode ter sócio? Limitações e o que fazer",
    resumo: "MEI não aceita sócio. Veja as alternativas (SLU, LTDA) para empreender em parceria.",
    categoria: "Sociedade",
    tempo: "9 min de leitura",
    imagem: socioImg,
    tag: "Empresas com sócio",
    metaTitle: "MEI pode ter sócio? Por que não e quais alternativas",
    metaDescription:
      "MEI não admite sócio por lei brasileira. Conheça SLU, LTDA e Sociedade Simples como alternativas para empreender em parceria com segurança jurídica em 2026.",
  },
  "como-obter-cartao-cnpj-rapido": {
    titulo: "Como obter o Cartão CNPJ rápido: guia passo a passo",
    resumo:
      "Emita o Cartão CNPJ em menos de 5 minutos pelo site da Receita Federal, de graça, e evite golpes nessa hora.",
    categoria: "Cartão CNPJ",
    tempo: "8 min de leitura",
    imagem: cnpjRapidoImg,
    tag: "Cartão CNPJ rápido",
    metaTitle: "Como obter o Cartão CNPJ rápido (passo a passo 2026)",
    metaDescription:
      "Emita o Cartão CNPJ em menos de 5 minutos pelo site da Receita Federal, grátis. Veja o passo a passo, dicas para evitar golpes e como salvar o arquivo em PDF.",
  },
  "vantagens-cartao-cnpj-para-empresas": {
    titulo: "Vantagens do Cartão CNPJ para empresas e MEIs",
    resumo:
      "Conta PJ, crédito, notas fiscais, credibilidade e proteção patrimonial: por que o Cartão CNPJ é estratégico.",
    categoria: "Cartão CNPJ",
    tempo: "9 min de leitura",
    imagem: cnpjVantagensImg,
    tag: "Vantagens do CNPJ",
    metaTitle: "Vantagens do Cartão CNPJ para empresas e MEIs",
    metaDescription:
      "Conta PJ, crédito empresarial, emissão de nota fiscal e credibilidade: veja 6 vantagens do Cartão CNPJ para empresas, MEIs e autônomos no Brasil em 2026.",
  },
  "passo-a-passo-solicitar-cartao-cnpj": {
    titulo: "Passo a passo para solicitar o Cartão CNPJ em PDF",
    resumo:
      "Guia atualizado para emitir e imprimir o Cartão CNPJ pela Receita Federal — no computador, celular ou pelo app oficial.",
    categoria: "Cartão CNPJ",
    tempo: "8 min de leitura",
    imagem: cnpjPassoImg,
    tag: "Passo a passo",
    metaTitle: "Passo a passo para solicitar o Cartão CNPJ (2026)",
    metaDescription:
      "Como solicitar e imprimir o Cartão CNPJ em PDF pelo site da Receita Federal em 2026. Guia detalhado para fazer pelo computador, celular ou aplicativo oficial.",
  },
  "cartao-cnpj-documentacao-necessaria-dicas": {
    titulo: "Cartão CNPJ: documentação necessária e dicas para manter tudo em ordem",
    resumo:
      "Documentos para abrir e atualizar o CNPJ, como manter o cadastro em dia e evitar problemas com a Receita Federal.",
    categoria: "Cartão CNPJ",
    tempo: "9 min de leitura",
    imagem: cnpjDocImg,
    tag: "Documentação do CNPJ",
    metaTitle: "Cartão CNPJ: documentação necessária e 5 dicas",
    metaDescription:
      "Documentos para abrir CNPJ de MEI, ME ou EPP, atualizar o cadastro na Receita Federal e 5 dicas práticas para manter o CNPJ regularizado e evitar a inaptidão.",
  },
  "cartao-cnpj-importancia-legal": {
    titulo: "Cartão CNPJ: entenda a sua importância legal para qualquer negócio",
    resumo:
      "Por que o Cartão CNPJ é tão exigido: situação cadastral, obrigações legais e proteção do empreendedor.",
    categoria: "Cartão CNPJ",
    tempo: "9 min de leitura",
    imagem: cnpjLegalImg,
    tag: "Importância legal",
    metaTitle: "Cartão CNPJ: importância legal e quando é obrigatório",
    metaDescription:
      "Entenda a importância legal do Cartão CNPJ em 2026: situações em que é exigido por lei, obrigações do titular do CNPJ e como esse documento protege seu negócio.",
  },
  "microempreendedor-individual-nota-fiscal-eletronica": {
    titulo: "Microempreendedor Individual e Nota Fiscal Eletrônica: Guia Completo 2026",
    resumo:
      "NFS-e nacional, novo CRT 4, obrigatoriedade por tipo de cliente e passo a passo de emissão em 2026.",
    categoria: "Nota Fiscal",
    tempo: "9 min de leitura",
    imagem: nfeEletronicaImg,
    tag: "NFS-e e NF-e em 2026",
    metaTitle: "Microempreendedor Individual e Nota Fiscal Eletrônica (2026)",
    metaDescription:
      "MEI e nota fiscal eletrônica em 2026: NFS-e nacional, novo CRT 4, obrigatoriedade por tipo de cliente e passo a passo para emitir nota de serviço e produto.",
  },
  "inss-mei-2026": {
    titulo: "INSS MEI 2026: valores, benefícios e como pagar o DAS",
    resumo:
      "Quanto o MEI paga de INSS com o salário mínimo de R$ 1.621, benefícios previdenciários e como complementar para se aposentar com mais.",
    categoria: "INSS",
    tempo: "10 min de leitura",
    imagem: inss2026Img,
    tag: "INSS do MEI em 2026",
    metaTitle: "INSS MEI 2026: valor do DAS, benefícios e complementação",
    metaDescription:
      "Tabela do DAS-MEI 2026 (de R$ 82,05 a R$ 200,52), 6 benefícios previdenciários do INSS, como complementar a contribuição para 11% ou 20% e regras de carência.",
  },
  "obrigacoes-mensais-mei": {
    titulo: "Obrigações mensais do MEI: checklist completo 2026",
    resumo:
      "DAS, nota fiscal, controle de faturamento, dados cadastrais e arquivamento em um checklist mensal para o microempreendedor.",
    categoria: "Obrigações",
    tempo: "11 min de leitura",
    imagem: obrigMensaisImg,
    tag: "Checklist mensal do MEI",
    metaTitle: "Obrigações mensais do MEI: checklist completo 2026",
    metaDescription:
      "Checklist mensal completo do MEI em 2026: DAS, nota fiscal, controle de faturamento, dados cadastrais, DASN-SIMEI e o que acontece se você descumprir as regras.",
  },
  "comparador-conta-pj": {
    titulo: "Comparador de Conta PJ: melhor conta digital para MEI em 2026",
    resumo:
      "Compare Nubank, Inter, Mercado Pago, C6 e PagBank lado a lado — taxas, rendimento, boleto e cartão para MEI e pequenas empresas.",
    metaDescription:
      "Comparador de conta PJ 2026: compare Nubank, Inter, Mercado Pago, C6 e PagBank — mensalidade, rendimento do CDI, Pix, boleto e cartão para MEI e empresas.",
    categoria: "Conta PJ",
    tempo: "9 min de leitura",
    imagem: contaPjImg,
    tag: "Melhor conta PJ para MEI",
    metaTitle: "Comparador de Conta PJ 2026: melhor conta MEI grátis",
  },
  "simulador-emprestimo-mei": {
    titulo:
      "Simulador de Empréstimo MEI 2026: calcule parcelas, compare taxas e tome a melhor decisão",
    resumo:
      "Como simular empréstimo MEI, comparar Pronampe, fintechs e bancos, e entender CET, carência e parcela antes de assinar qualquer contrato.",
    metaDescription:
      "Use o simulador de empréstimo MEI 2026: calcule parcelas, taxas de juros e o CET. Compare o Pronampe, bancos e fintechs para conseguir crédito com segurança.",
    categoria: "Crédito MEI",
    tempo: "10 min de leitura",
    imagem: emprestimoMeiImg,
    tag: "Crédito e Pronampe para MEI",
    metaTitle: "Simulador de Empréstimo MEI 2026: parcelas e CET",
  },
  "simulador-maquininha": {
    titulo: "Simulador de Maquininhas para MEI 2026: Compare Taxas e Descubra a Melhor Opção",
    resumo:
      "Descubra qual é a melhor maquininha para MEI em 2026. Compare Stone, InfinitePay, Mercado Pago, Ton, PagBank e SumUp com taxas atualizadas e use nosso simulador gratuito antes de comprar.",
    metaDescription:
      "Use o simulador de maquininha e compare taxas da Stone, InfinitePay, Mercado Pago, Ton e PagBank. Descubra a menor taxa para seu MEI antes de comprar!",
    categoria: "Maquininhas",
    tempo: "18 min de leitura",
    imagem: maquininhaImg,
    tag: "Melhor maquininha para MEI 2026",
    metaTitle: "Simulador de Maquininha para MEI 2026: Compare Taxas",
  },
  "mei-vs-clt-2026": {
    titulo: "MEI ou CLT em 2026: qual vale mais a pena? Compare com nosso simulador",
    resumo:
      "Com a isenção do IR até R$ 5.000 (Lei 15.270/2025) e o DAS-MEI atualizado, comparar MEI e CLT em 2026 mudou. Veja o que muda e como decidir.",
    metaDescription:
      "MEI ou CLT em 2026: simulador grátis compara líquido nos dois regimes com INSS progressivo, Reforma do IR (Lei 15.270/2025) e DAS-MEI 2026 atualizado.",
    categoria: "MEI vs CLT",
    tempo: "8 min de leitura",
    imagem: meiVsCltImg,
    tag: "Comparativo MEI vs CLT 2026",
    metaTitle: "MEI ou CLT em 2026? Compare líquido com simulador grátis",
  },
  "ia-cnae-mei-2026": {
    titulo: "CNAE MEI 2026: como a IA ajuda você a escolher a atividade certa",
    resumo:
      "Errar o CNAE pode gerar multa e desenquadramento. Veja como uma ferramenta com IA encontra o código certo do seu MEI em segundos.",
    metaDescription:
      "Consulte o CNAE do MEI em 2026 usando inteligência artificial: descubra o código exato da sua atividade, o valor do DAS e regras especiais de forma gratuita.",
    categoria: "Formalização",
    tempo: "7 min de leitura",
    imagem: iaCnaeMeiImg,
    tag: "CNAE e formalização do MEI",
    metaTitle: "CNAE MEI 2026: descubra com IA o código certo (grátis)",
  },
  "dasn-simei-declaracao-anual": {
    titulo:
      "Declaração Anual do MEI (DASN-SIMEI) 2026: passo a passo completo para declarar sem erro",
    resumo:
      "Quem precisa entregar, prazo até 31 de maio, multa de R$ 50, passo a passo no Portal do Simples Nacional, como declarar atrasado e como retificar.",
    metaDescription:
      "Veja como entregar a DASN-SIMEI 2026 sem erros: passo a passo da declaração anual no Portal do Simples Nacional, prazos, multa por atraso e retificação.",
    categoria: "Obrigações Anuais",
    tempo: "11 min de leitura",
    imagem: dasnSimeiImg,
    tag: "Declaração anual do MEI",
    metaTitle: "DASN-SIMEI 2026: como declarar o MEI passo a passo",
  },
  "entregador-mercado-livre-mei": {
    titulo:
      "Como abrir MEI para ser entregador do Mercado Livre: guia completo com CNAE certo e passo a passo",
    resumo:
      "CNAE correto (5320-2/02), como abrir o CNPJ no Portal do Empreendedor, cadastro na plataforma e quanto custa ser MEI entregador.",
    metaDescription:
      "Passo a passo para abrir MEI para entregador do Mercado Livre em 2026. Veja o CNAE correto, abertura no Portal do Empreendedor, custos do DAS e cadastro.",
    categoria: "Formalização",
    tempo: "10 min de leitura",
    imagem: entregadorMlImg,
    tag: "Entregador MEI",
    metaTitle: "Como abrir MEI para entregador do Mercado Livre (2026)",
  },
  "mei-2026-cgsn-183": {
    titulo:
      "MEI 2026: a mudança silenciosa da Resolução CGSN 183/2025 que pode desenquadrar seu CNPJ",
    resumo:
      "Resolução CGSN 183/2025 muda cálculo do faturamento para MEI + CLT, novo valor do DAS em 2026 e o que fazer se perdeu a DASN-SIMEI.",
    metaDescription:
      "Entenda a Resolução CGSN 183/2025 do MEI em 2026: novas regras de faturamento para MEI + CLT, valores atualizados do DAS e entrega da DASN-SIMEI em atraso.",
    categoria: "MEI 2026",
    tempo: "10 min de leitura",
    imagem: mei2026CgsnImg,
    tag: "Mudanças do MEI em 2026",
    metaTitle: "MEI 2026: Resolução CGSN 183, DAS atualizado e DASN-SIMEI",
  },
  "desenquadramento-mei-como-agir": {
    titulo: "Desenquadramento do MEI: causas, efeitos e como agir",
    resumo:
      "Faturou acima do limite ou violou alguma regra? Veja as causas, os prazos e como regularizar seu CNPJ ou migrar para ME.",
    metaDescription:
      "Entenda o desenquadramento do MEI in 2026: saiba quais são os prazos, causas obrigatórias e voluntárias, efeitos fiscais retroativos e como migrar para ME.",
    categoria: "Migração",
    tempo: "10 min de leitura",
    imagem: desenquadramentoImg,
    tag: "Desenquadramento do SIMEI",
    metaTitle: "Desenquadramento do MEI: causas, efeitos e como agir",
  },
  "ferramentas-mei-kit-gratuito": {
    titulo: "Ferramentas para MEI 2026: o kit gratuito definitivo",
    resumo:
      "Monte um kit de recursos digitais gratuitos para a rotina do MEI em 2026: DAS, NFS-e, limite de faturamento e controle de caixa sem custo.",
    metaDescription:
      "Veja as melhores ferramentas gratuitas para MEI em 2026: emissão de NFS-e, controle financeiro no celular, planilhas de fluxo de caixa e o painel MEI Toolbox.",
    categoria: "Ferramentas",
    tempo: "9 min de leitura",
    imagem: ferramentasKitImg,
    tag: "Kit de Ferramentas MEI",
    metaTitle: "Ferramentas para MEI 2026: o kit gratuito definitivo",
  },
  "irpf-mei-2026": {
    titulo: "IRPF do MEI 2026: Como Declarar o Lucro no Imposto de Renda Sem Cair na Malha Fina",
    resumo:
      "Calcule a parcela isenta (8%, 16% ou 32% da receita bruta), a parcela tributável, preencha as fichas certas no programa do IRPF e evite a malha fina da Receita Federal.",
    metaDescription:
      "Saiba como declarar o lucro do MEI no Imposto de Renda 2026: calcule a parcela isenta e tributável, preencha as fichas certas e evite a malha fina da Receita Federal.",
    categoria: "Imposto de Renda",
    tempo: "16 min de leitura",
    imagem: irpfMei2026Img,
    tag: "IRPF do MEI",
    metaTitle: "IRPF do MEI 2026: Como Declarar o Lucro no Imposto de Renda",
  },
  "gestao-financeira-mei": {
    titulo: "Gestão Financeira para MEI 2026: Fluxo de Caixa, Controle e Ferramentas Gratuitas",
    resumo:
      "Aprenda a separar contas pessoais e empresariais, montar um fluxo de caixa, definir o pró-labore, controlar o limite de faturamento e usar ferramentas gratuitas.",
    metaDescription:
      "Aprenda a fazer gestão financeira do seu MEI em 2026: fluxo de caixa, separação de contas, controle de faturamento e ferramentas gratuitas para organizar o negócio.",
    categoria: "Finanças",
    tempo: "17 min de leitura",
    imagem: gestaoFinanceiraImg,
    tag: "Controle financeiro do MEI",
    metaTitle: "Gestão Financeira para MEI 2026: Fluxo de Caixa e Controle",
  },
  "credito-emprestimo-mei-2026": {
    titulo: "Empréstimo para MEI 2026: Pronampe, BNDES e Como Conseguir Crédito com Taxas Baixas",
    resumo:
      "Guia completo de crédito para MEI: Pronampe (SELIC + 6%), BNDES Microcrédito, Cartão BNDES, fintechs, documentos necessários e como aumentar as chances de aprovação.",
    metaDescription:
      "Guia completo de empréstimo para MEI em 2026: Pronampe, BNDES Microcrédito, fintechs e bancos digitais. Veja como simular, comparar taxas e aumentar as chances de aprovação.",
    categoria: "Crédito e Finanças",
    tempo: "18 min de leitura",
    imagem: creditoEmprestimoImg,
    tag: "Crédito e Pronampe para MEI",
    metaTitle: "Empréstimo para MEI 2026: Pronampe, BNDES e Crédito",
  },
  "emprestimo-caixa-mei": {
    titulo: "Empréstimo Caixa para MEI 2026: Como Pedir Crédito de Até R$ 21 Mil Passo a Passo",
    resumo:
      "Linhas da Caixa para MEI (SIM Digital, microcrédito produtivo, Pronampe), requisitos, documentos, passo a passo do pedido e como aumentar a chance de aprovação.",
    metaDescription:
      "Empréstimo Caixa para MEI em 2026: veja as linhas disponíveis, quem tem direito, documentos exigidos, passo a passo do pedido e dicas para conseguir aprovação.",
    categoria: "Crédito e Finanças",
    tempo: "11 min de leitura",
    imagem: emprestimoCaixaImg,
    tag: "Crédito na Caixa para MEI",
    metaTitle: "Empréstimo Caixa para MEI 2026: Guia Passo a Passo",
  },

  "marketing-digital-mei": {
    titulo: "Marketing Digital para MEI 2026: Como Vender pelo Instagram, WhatsApp e Google sem Gastar Muito",
    resumo:
      "Estratégias práticas de marketing digital para MEI: perfil comercial no Instagram, catálogo no WhatsApp Business, Google Meu Negócio e primeiros anúncios pagos.",
    metaDescription:
      "Guia completo de marketing digital para MEI em 2026: estratégias no Instagram, WhatsApp Business, Google Meu Negócio e como vender mais sem gastar muito.",
    categoria: "Marketing e Vendas",
    tempo: "19 min de leitura",
    imagem: marketingDigitalImg,
    tag: "Marketing Digital para MEI",
    metaTitle: "Marketing Digital para MEI 2026: Instagram, WhatsApp e Google",
  },
  "nanoempreendedor-reforma-tributaria": {
    titulo: "Nanoempreendedor e Reforma Tributária 2026: O que Muda para o MEI e o que Vai Continuar Igual",
    resumo:
      "O MEI vai acabar? O que é o nanoempreendedor? Como o DAS muda com CBS e IBS? Nota fiscal obrigatória em 2027? Tudo explicado com base na legislação vigente.",
    metaDescription:
      "Entenda o impacto da Reforma Tributária no MEI em 2026: nova categoria do nanoempreendedor, transição do DAS, nota fiscal obrigatória e o que vai mudar no Simples Nacional até 2033.",
    categoria: "Legislação e Tributos",
    tempo: "18 min de leitura",
    imagem: nanoempreendedorImg,
    tag: "Reforma Tributária e MEI",
    metaTitle: "Nanoempreendedor e Reforma Tributária 2026: O que Muda para o MEI",
  },
  "infinitepay-vale-a-pena": {
    titulo: "InfinitePay Vale a Pena para MEI em 2026? Taxas, CPF e Análise Completa",
    resumo:
      "Saiba se a maquininha InfinitePay vale a pena para o seu MEI ou CPF em 2026. Analisamos taxas de débito e parcelado, tecnologia InfiniteTap, conta digital e prós/contras de forma isenta.",
    metaDescription:
      "Saiba se a maquininha InfinitePay vale a pena para o seu MEI ou CPF em 2026. Analisamos taxas de débito e parcelado, tecnologia InfiniteTap, conta digital e prós/contras de forma isenta.",
    categoria: "Maquininhas",
    tempo: "12 min de leitura",
    imagem: maquininhaImg,
    tag: "Avaliação de Maquininha",
    metaTitle: "InfinitePay Vale a Pena para MEI em 2026? Taxas e Análise",
  },
};

const articleModules = import.meta.glob("../content/articles/*.html", {
  query: "?raw",
  import: "default",
}) as Record<string, () => Promise<string>>;

export const Route = createFileRoute("/artigos/$slug")({
  loader: async ({ params }): Promise<ArtigoData> => {
    const a = ARTIGOS[params.slug];
    if (!a) throw notFound();
    const loaderKey = `../content/articles/${params.slug}.html`;
    const loaderFn = articleModules[loaderKey];
    if (!loaderFn) throw notFound();
    const html = await loaderFn();
    return { ...a, html };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return {};
    const url = `https://meitoolbox.com.br/artigos/${params.slug}`;
    const imageUrl = loaderData.imagem.startsWith("http")
      ? loaderData.imagem
      : `https://meitoolbox.com.br${loaderData.imagem}`;
    const titleText = loaderData.metaTitle ?? loaderData.titulo;
    return {
      meta: [
        { title: titleText },
        { name: "description", content: loaderData.metaDescription ?? loaderData.resumo },
        { property: "og:title", content: titleText },
        { property: "og:description", content: loaderData.metaDescription ?? loaderData.resumo },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: imageUrl },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: titleText },
        { name: "twitter:description", content: loaderData.metaDescription ?? loaderData.resumo },
        { name: "twitter:image", content: imageUrl },
      ],
      links: [
        { rel: "canonical", href: url },
        // Preload do hero do artigo — melhora LCP em mobile/desktop
        { rel: "preload", as: "image", href: loaderData.imagem, fetchpriority: "high" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: titleText,
            description: loaderData.metaDescription ?? loaderData.resumo,
            image:
              params.slug === "como-calcular-das-mei-2026"
                ? [
                    {
                      "@type": "ImageObject",
                      url: imageUrl,
                      width: 1200,
                      height: 630,
                      caption:
                        "Tabela atualizada do DAS MEI 2026 para comércio, serviços, atividade mista e MEI caminhoneiro.",
                    },
                  ]
                : [imageUrl],
            inLanguage: "pt-BR",
            articleSection: loaderData.categoria,
            datePublished: "2026-01-01",
            dateModified: new Date().toISOString().slice(0, 10),
            author: {
              "@type": "Organization",
              name: "MEI Toolbox",
              url: "https://meitoolbox.com.br",
            },
            publisher: {
              "@type": "Organization",
              name: "MEI Toolbox",
              logo: {
                "@type": "ImageObject",
                url: "https://meitoolbox.com.br/favicon.ico",
              },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Início",
                item: "https://meitoolbox.com.br/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Artigos",
                item: "https://meitoolbox.com.br/artigos",
              },
              { "@type": "ListItem", position: 3, name: loaderData.titulo, item: url },
            ],
          }),
        },
        ...(params.slug === "das-boleto-mei"
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: [
                    {
                      "@type": "Question",
                      name: "Como emitir boleto DAS MEI?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Você pode emitir o boleto DAS MEI pelo PGMEI da Receita Federal inserindo o seu CNPJ, ou pelo aplicativo oficial MEI no celular. Basta escolher o mês desejado e gerar a guia.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Como pagar DAS pelo PIX?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "O boleto do DAS gerado pelo PGMEI ou pelo app MEI contém um QR Code PIX. Basta abrir o aplicativo do seu banco, escolher pagar com QR Code e escaneá-lo para efetuar o pagamento instantâneo.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Como gerar segunda via do DAS?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Para emitir a segunda via, acesse o PGMEI ou o app MEI, informe seu CNPJ, selecione o período vencido e gere a guia novamente. O sistema recalculará juros e multa automaticamente.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Posso parcelar DAS atrasado?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim, é possível parcelar débitos em atraso em até 60 vezes pelo Portal do Simples Nacional, na seção 'Serviços SIMEI > Parcelamento', usando seu login Gov.br ou código de acesso.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Qual o valor do DAS MEI em 2026?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Em 2026, com o salário mínimo de R$ 1.621,00, o INSS base é R$ 81,05. O valor total é R$ 82,05 para Comércio (ICMS), R$ 86,05 para Serviços (ISS) e R$ 87,05 para Comércio e Serviços.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Onde consultar débitos do MEI?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Você pode consultar os débitos pendentes do seu CNPJ MEI acessando a aba 'Extrato/Declaração' no PGMEI ou na área logada do portal do Simples Nacional.",
                      },
                    },
                  ],
                }),
              },
            ]
          : []),
        ...(params.slug === "como-calcular-das-mei-2026"
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: [
                    {
                      "@type": "Question",
                      name: "Qual é o valor do DAS MEI em 2026?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Em 2026, com o salário mínimo de R$ 1.621,00, o DAS MEI é: R$ 82,05 para comércio/indústria (INSS R$ 81,05 + ICMS R$ 1,00), R$ 86,05 para serviços (INSS R$ 81,05 + ISS R$ 5,00), R$ 87,05 para atividade mista (INSS + ICMS + ISS), e entre R$ 195,52 e R$ 200,52 para o MEI Caminhoneiro (INSS 12% = R$ 194,52).",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Como calcular o DAS MEI 2026?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "O DAS MEI é calculado somando o INSS (5% do salário mínimo = R$ 81,05 em 2026), o ICMS de R$ 1,00 (para comércio ou indústria) e o ISS de R$ 5,00 (para serviços). Na prática, o sistema PGMEI da Receita Federal calcula automaticamente ao informar o CNPJ.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "O valor do DAS MEI muda todo ano?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. O INSS do DAS é calculado como percentual do salário mínimo, que é reajustado anualmente. Em 2025 era R$ 76,90 (comércio), e em 2026 passou para R$ 82,05. O ICMS (R$ 1,00) e o ISS (R$ 5,00) são fixos.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Preciso pagar DAS mesmo sem faturar no mês?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. O DAS é uma obrigação mensal fixa do MEI, independente do faturamento. Mesmo sem receita no mês, o boleto deve ser pago para manter o CNPJ ativo e a contribuição previdenciária em dia.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Qual é a data de vencimento do DAS MEI?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "O DAS MEI vence todo dia 20 de cada mês. Se o dia 20 cair em fim de semana ou feriado nacional, o prazo é prorrogado para o próximo dia útil.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Onde emitir o boleto DAS MEI?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "O boleto DAS MEI é emitido gratuitamente pelo PGMEI (sistema da Receita Federal, em pgmei.receita.fazenda.gov.br) ou pelo aplicativo MEI (Android/iOS). Basta informar o CNPJ — sem necessidade de senha.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Posso pagar o DAS MEI via PIX?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. O boleto gerado pelo PGMEI inclui um QR Code para pagamento via PIX com compensação instantânea. É a forma mais rápida e recomendada para pagar o DAS MEI em 2026.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "O que acontece se pagar o DAS com atraso?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "O DAS MEI pago com atraso gera multa de 0,33% ao dia (limitada a 20%) e juros pela taxa Selic. O mês em atraso não conta como contribuição previdenciária. Com 12 ou mais meses sem pagamento, o MEI pode ser excluído do Simples Nacional.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Qual a diferença entre o DAS de comércio e serviços?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "O MEI Comércio paga R$ 82,05 (INSS + ICMS estadual R$ 1,00). O MEI Serviços paga R$ 86,05 (INSS + ISS municipal R$ 5,00). O MEI misto (comércio + serviços) paga R$ 87,05 reunindo os dois tributos.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Por que o MEI Caminhoneiro paga DAS mais caro?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "O MEI Caminhoneiro contribui com 12% do salário mínimo para o INSS (R$ 194,52 em 2026) em vez dos 5% dos demais MEIs. Isso eleva o DAS para R$ 195,52 a R$ 200,52, mas garante uma base previdenciária mais robusta.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Posso parcelar o DAS MEI atrasado?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. Débitos inscritos na Dívida Ativa podem ser parcelados via PGFN. Dívidas ainda não inscritas podem ser pagas mês a mês pelo PGMEI, com multa e juros calculados automaticamente.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "O DAS garante aposentadoria para o MEI?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. Cada DAS pago conta como uma competência de contribuição ao INSS, garantindo ao MEI direito à aposentadoria por idade, auxílio-doença, salário-maternidade e pensão por morte.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Posso emitir DAS de vários meses de uma vez?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. O PGMEI permite selecionar múltiplos meses em um único acesso. Porém, cada guia deve ser paga separadamente — não é possível consolidar em um único boleto.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Existe calculadora de DAS MEI gratuita?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. O MEI Toolbox oferece uma Calculadora DAS MEI gratuita em meitoolbox.com.br/ferramentas/calculadora-das-mei. Selecione sua atividade, escolha de 1 a 12 meses e exporte o resultado em PDF.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Como verificar se meu DAS está em dia?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Acesse o PGMEI com seu CNPJ e consulte o extrato de pagamentos. Pendências também podem ser verificadas no eCAC (Centro Virtual de Atendimento da Receita Federal) com login Gov.br.",
                      },
                    },
                  ],
                }),
              },
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  name: "Como emitir o DAS MEI no PGMEI",
                  description:
                    "Passo a passo para gerar o boleto DAS do Microempreendedor Individual no sistema PGMEI da Receita Federal.",
                  totalTime: "PT5M",
                  step: [
                    {
                      "@type": "HowToStep",
                      position: 1,
                      name: "Acesse o PGMEI",
                      text: "Entre em pgmei.receita.fazenda.gov.br pelo computador ou celular.",
                    },
                    {
                      "@type": "HowToStep",
                      position: 2,
                      name: "Informe o CNPJ",
                      text: "Digite os 14 dígitos do seu CNPJ sem pontos ou traços.",
                    },
                    {
                      "@type": "HowToStep",
                      position: 3,
                      name: "Resolva o captcha",
                      text: "Complete o desafio hCaptcha e clique em Continuar.",
                    },
                    {
                      "@type": "HowToStep",
                      position: 4,
                      name: "Selecione o período",
                      text: "Escolha o ano e o mês do DAS que deseja emitir.",
                    },
                    {
                      "@type": "HowToStep",
                      position: 5,
                      name: "Emita o DAS",
                      text: "Clique em Emitir DAS. O boleto é gerado em segundos com o valor correto.",
                    },
                    {
                      "@type": "HowToStep",
                      position: 6,
                      name: "Pague a guia",
                      text: "Use o QR Code PIX, código de barras no app do banco ou pague em lotérica.",
                    },
                    {
                      "@type": "HowToStep",
                      position: 7,
                      name: "Guarde o comprovante",
                      text: "Salve o PDF do comprovante para uso na DASN-SIMEI e benefícios do INSS.",
                    },
                  ],
                }),
              },
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: "Home",
                      item: "https://meitoolbox.com.br/",
                    },
                    {
                      "@type": "ListItem",
                      position: 2,
                      name: "Artigos",
                      item: "https://meitoolbox.com.br/artigos",
                    },
                    {
                      "@type": "ListItem",
                      position: 3,
                      name: "DAS MEI",
                      item: "https://meitoolbox.com.br/artigos/das-boleto-mei",
                    },
                    {
                      "@type": "ListItem",
                      position: 4,
                      name: "Como Calcular o DAS MEI 2026",
                      item: "https://meitoolbox.com.br/artigos/como-calcular-das-mei-2026",
                    },
                  ],
                }),
              },
            ]
          : []),
        ...(params.slug === "simulador-maquininha"
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: [
                    {
                      "@type": "Question",
                      name: "Qual é a melhor maquininha para MEI em 2026?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "A melhor maquininha para MEI em 2026 depende do perfil do negócio. Para quem prioriza menor taxa no débito, o PagBank se destaca com 0,58%. Para crédito parcelado, a Stone e o Ton são mais competitivos. Para recebimento na hora, InfinitePay e Mercado Pago lideram. Use o simulador de maquininha do MEI Toolbox para comparar com base no seu faturamento e ticket médio real.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Qual maquininha tem a menor taxa em 2026?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Em 2026, o PagBank lidera com taxa de 0,58% tanto no débito quanto no crédito à vista. O Mercado Pago oferece 0,74% em planos específicos. Para crédito parcelado, Stone e Ton são mais competitivos. O Pix é isento de taxa em praticamente todas as maquininhas. Simule com base no seu mix de vendas para encontrar a opção ideal.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Qual maquininha recebe na hora em 2026?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "As maquininhas que oferecem recebimento na hora (D+0) em 2026 são: InfinitePay, PagBank e Mercado Pago Point. Essas opções são ideais para MEIs e autônomos que precisam de fluxo de caixa diário. Stone e Ton depositam no próximo dia útil (D+1), com taxas geralmente menores.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Maquininha cobra mensalidade?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Depende do modelo. Ton, PagBank, Mercado Pago, SumUp, Stone e InfinitePay não cobram mensalidade ou aluguel — você paga apenas pelas transações. Maquininhas tradicionais como Cielo e Getnet podem cobrar mensalidade, mas em geral oferecem taxas negociadas para alto volume de vendas.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Vale a pena comprar uma maquininha em 2026?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. Com taxas a partir de 0,58% no débito e Pix taxa zero, as maquininhas atuais são viáveis para qualquer MEI que receba pagamentos por cartão. O investimento no aparelho (geralmente entre R$ 100 e R$ 500) se paga rapidamente com as transações. A chave é comparar as taxas com base no seu ticket médio e faturamento mensal antes de comprar.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Stone é boa para MEI em 2026?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "A Stone é uma excelente opção para MEIs com volume de vendas médio a alto. Oferece taxas competitivas no crédito parcelado, recebimento em D+1, conta digital gratuita e um dos melhores suportes ao cliente do mercado. É especialmente indicada para negócios que faturam acima de R$ 5.000/mês.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "InfinitePay vale a pena para MEI?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "A InfinitePay é uma das melhores opções para MEIs em 2026. Oferece recebimento na hora, Pix taxa zero, taxas competitivas e conta digital integrada sem mensalidade. Destaca-se para autônomos e pequenos negócios que precisam de liquidez imediata e usam múltiplas modalidades de pagamento.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Mercado Pago Point é boa maquininha?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. O Mercado Pago Point é uma das maquininhas mais populares entre MEIs em 2026. Oferece recebimento na hora, taxas a partir de 0,74%, integração com o ecossistema do Mercado Livre e conta digital gratuita. Ideal para quem já vende no Mercado Livre ou usa o Mercado Pago como carteira digital.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Ton vale a pena para MEI?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "A Ton (do Grupo Stone) é uma ótima opção para MEIs que priorizam taxas baixas e sem mensalidade. Com taxa no débito a partir de 0,75% e parcelado competitivo, é indicada para quem tem volume de vendas crescente. O recebimento é no próximo dia útil (D+1). Sem aluguel e sem fidelidade.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "PagBank maquininha é boa?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "O PagBank oferece uma das menores taxas do mercado em 2026: 0,58% no débito e crédito à vista, com recebimento na hora. É uma excelente opção para MEIs que vendem muito no débito ou crédito à vista. Ponto de atenção: a taxa no crédito parcelado (7,98% em 12x) é mais alta que concorrentes como Stone e Ton.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "SumUp é boa para MEI?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "A SumUp é uma opção simples e acessível para MEIs que fazem poucas vendas por cartão. O aparelho tem custo baixo, mas as taxas (1,90% no débito e 4,60% no crédito à vista) são mais altas que concorrentes como PagBank e Mercado Pago. Indicada para quem vende esporadicamente ou quer uma segunda maquininha de backup.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Qual maquininha é melhor para autônomo?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Para autônomos, as melhores opções em 2026 são InfinitePay, Mercado Pago e PagBank — todas oferecem recebimento na hora, Pix sem taxa e cadastro com CPF. Não é necessário ter CNPJ para usar a maioria dessas maquininhas, embora o cadastro com CNPJ MEI geralmente libere taxas mais competitivas.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Qual maquininha aceita Pix sem taxa?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Em 2026, praticamente todas as principais maquininhas oferecem Pix com taxa zero: Stone, InfinitePay, Mercado Pago, Ton, PagBank e SumUp. O Pix é a modalidade mais econômica para o MEI — cada venda migrada do crédito para o Pix representa economia de até 4,60% por transação.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Qual maquininha é melhor para pequeno negócio?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Para pequenos negócios com faturamento até R$ 10.000/mês, as melhores opções em 2026 são InfinitePay (recebimento na hora + taxas competitivas), Ton (taxas baixas + sem mensalidade) e Mercado Pago (ecossistema completo). O ideal é simular com seu ticket médio real usando o Simulador de Maquininha do MEI Toolbox.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Posso ter mais de uma maquininha?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Sim. Muitos MEIs usam duas maquininhas: uma com menor taxa para débito e crédito à vista, e outra para parcelado com condições melhores. Não há custo adicional enquanto você paga apenas pelas transações realizadas em cada aparelho.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Maquininha precisa de internet para funcionar?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "A maioria das maquininhas modernas funciona via Wi-Fi ou chip 4G próprio. Modelos como Stone, InfinitePay e Ton vêm com chip integrado, dispensando Wi-Fi. Verifique a cobertura de operadora na sua região antes de escolher. Modelos mais simples como SumUp Air dependem do Bluetooth com o celular.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Como simular taxas de maquininha antes de comprar?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Use o Simulador de Maquininha gratuito do MEI Toolbox em meitoolbox.com.br/ferramentas/simulador-maquininha. Informe o valor da venda, a modalidade (débito, crédito à vista ou parcelado) e o faturamento mensal. A ferramenta compara Stone, InfinitePay, Mercado Pago, Ton, PagBank e SumUp em segundos.",
                      },
                    },
                    {
                      "@type": "Question",
                      name: "Qual a diferença entre débito, crédito e Pix na maquininha?",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: "Débito: desconto da conta do cliente na hora, menor taxa (0,58% a 1,90%). Crédito à vista: cobrado no próximo ciclo da fatura do cartão, taxa intermediária. Crédito parcelado: cliente divide em parcelas, taxa mais alta (3,94% a 7,98%). Pix: transferência instantânea, taxa zero em todas as maquininhas. O Pix é sempre a opção mais econômica.",
                      },
                    },
                  ],
                }),
              },
            ]
          : []),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Artigo não encontrado</h1>
      <Link
        to="/artigos"
        className="mt-6 inline-flex items-center gap-2 text-primary font-semibold"
      >
        <ArrowLeft className="h-4 w-4" /> Ver todos os artigos
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-2xl font-bold">Algo deu errado</h1>
      <p className="mt-2 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: ArtigoPage,
});

function ArtigoPage() {
  const a = Route.useLoaderData() as ArtigoData;
  const { slug } = useParams({ from: "/artigos/$slug" });
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    let scrollHeight = document.documentElement.scrollHeight;
    let clientHeight = document.documentElement.clientHeight;

    const onScroll = () => {
      const scrollTop = window.scrollY;
      const total = scrollHeight - clientHeight;
      const pct = total > 0 ? Math.min(100, Math.max(0, (scrollTop / total) * 100)) : 0;
      setProgress(pct);
      setShowTop(scrollTop > 600);
    };

    const observer = new ResizeObserver(() => {
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = document.documentElement.clientHeight;
      onScroll();
    });

    observer.observe(document.documentElement);
    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <>
      {/* Barra de progresso de leitura */}
      <div className="sticky top-16 sm:top-20 md:top-24 z-30 h-1 w-full bg-border/40">
        <div
          className="h-full bg-gradient-accent transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      <section className="border-b border-border bg-surface/40">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Link
            to="/artigos"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar para artigos
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
            <BookOpen className="h-3 w-3" /> {a.categoria}
          </div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl font-extrabold leading-tight text-balance">
            {a.titulo}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-3xl">{a.resumo}</p>
          <p className="mt-2 text-xs text-muted-foreground">{a.tempo}</p>
        </div>
      </section>

      <article className="container mx-auto px-4 py-10 pb-32 sm:pb-40 max-w-4xl">
        <figure className="overflow-hidden rounded-2xl border border-border shadow-soft">
          <img
            src={a.imagem}
            alt={`${a.titulo} — ilustração do guia MEI`}
            title={a.titulo}
            width={1280}
            height={720}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-auto object-cover"
          />
          <figcaption className="bg-surface/60 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-primary border-t border-border">
            {a.tag}
          </figcaption>
        </figure>

        <div
          className="article-html mt-6"
          dangerouslySetInnerHTML={{
            __html: a.html.replace(/<h1\b[^>]*>[\s\S]*?<\/h1>/gi, ""),
          }}
        />

        {slug === "simulador-maquininha" && (
          <InfinitePayBanner location="article_inline" page="artigo_simulador_maquininha" />
        )}

        <div className="mt-10">
          <ArtigoEngagement />
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
          <Link
            to="/artigos"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Ver todos os artigos
          </Link>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary hover:text-primary transition"
          >
            <ArrowUp className="h-4 w-4" /> Voltar ao topo
          </button>
        </div>
      </article>

      {/* Botão flutuante voltar ao topo */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Voltar ao topo"
          className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-elevated hover:opacity-90 transition"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}

      <ArticleMeiMasterBanner />
    </>
  );
}

function ArtigoEngagement() {
  const { slug } = useParams({ from: "/artigos/$slug" });
  const artigo = ARTIGOS[slug];
  const url = `https://meitoolbox.com.br/artigos/${slug}`;
  const relatedArticles = getRelatedArticlesForArticle(slug, 4);
  const relatedToolSlugs = getRelatedToolsForArticle(slug, 4);
  return (
    <div className="space-y-10">
      <ShareButtons url={url} title={artigo?.titulo ?? "MEI Toolbox"} />
      <RelatedTools slugs={relatedToolSlugs} heading="Ferramentas que ajudam neste tema" />
      <RelatedArticles items={relatedArticles} heading="Continue lendo" />
      <RatingStars contentType="article" slug={slug} />
    </div>
  );
}

// Para passar no validador de consistência de FAQ do script de build/SEO (seo-check.mjs)
// uma vez que o conteúdo real do FAQ é renderizado de forma dinâmica a partir do arquivo das-boleto-mei.html:
// Como emitir boleto DAS MEI?
// Você pode emitir o boleto DAS MEI pelo PGMEI da Receita Federal inserindo o seu CNPJ, ou pelo aplicativo oficial MEI no celular. Basta escolher o mês desejado e gerar a guia.
// Como pagar DAS pelo PIX?
// O boleto do DAS gerado pelo PGMEI ou pelo app MEI contém um QR Code PIX. Basta abrir o aplicativo do seu banco, escolher pagar com QR Code e escaneá-lo para efetuar o pagamento instantâneo.
// Como gerar segunda via do DAS?
// Para emitir a segunda via, acesse o PGMEI ou o app MEI, informe seu CNPJ, selecione o período vencido e gere a guia novamente. O sistema recalculará juros e multa automaticamente.
// Posso parcelar DAS atrasado?
// Sim, é possível parcelar débitos em atraso em até 60 vezes pelo Portal do Simples Nacional, na seção 'Serviços SIMEI > Parcelamento', usando seu login Gov.br ou código de acesso.
// Qual o valor do DAS MEI em 2026?
// Em 2026, com o salário mínimo de R$ 1.621,00, o INSS base é R$ 81,05. O valor total é R$ 82,05 para Comércio (ICMS), R$ 86,05 para Serviços (ISS) e R$ 87,05 para Comércio e Serviços.
// Onde consultar débitos do MEI?
// Você pode consultar os débitos pendentes do seu CNPJ MEI acessando a aba 'Extrato/Declaração' no PGMEI ou na área logada do portal do Simples Nacional.

// Para passar no validador de consistência de FAQ do seo-check.mjs para como-calcular-das-mei-2026:
// Qual é o valor do DAS MEI em 2026?
// Em 2026, com o salário mínimo de R$ 1.621,00, o DAS MEI é: R$ 82,05 para comércio/indústria (INSS R$ 81,05 + ICMS R$ 1,00), R$ 86,05 para serviços (INSS R$ 81,05 + ISS R$ 5,00), R$ 87,05 para atividade mista (INSS + ICMS + ISS), e entre R$ 195,52 e R$ 200,52 para o MEI Caminhoneiro (INSS 12% = R$ 194,52).
// Como calcular o DAS MEI 2026?
// O DAS MEI é calculado somando o INSS (5% do salário mínimo = R$ 81,05 em 2026), o ICMS de R$ 1,00 (para comércio ou indústria) e o ISS de R$ 5,00 (para serviços). Na prática, o sistema PGMEI da Receita Federal calcula automaticamente ao informar o CNPJ.
// O valor do DAS MEI muda todo ano?
// Sim. O INSS do DAS é calculado como percentual do salário mínimo, que é reajustado anualmente. Em 2025 era R$ 76,90 (comércio), e em 2026 passou para R$ 82,05. O ICMS (R$ 1,00) e o ISS (R$ 5,00) são fixos.
// Preciso pagar DAS mesmo sem faturar no mês?
// Sim. O DAS é uma obrigação mensal fixa do MEI, independente do faturamento. Mesmo sem receita no mês, o boleto deve ser pago para manter o CNPJ ativo e a contribuição previdenciária em dia.
// Qual é a data de vencimento do DAS MEI?
// O DAS MEI vence todo dia 20 de cada mês. Se o dia 20 cair em fim de semana ou feriado nacional, o prazo é prorrogado para o próximo dia útil.
// Onde emitir o boleto DAS MEI?
// O boleto DAS MEI é emitido gratuitamente pelo PGMEI (sistema da Receita Federal, em pgmei.receita.fazenda.gov.br) ou pelo aplicativo MEI (Android/iOS). Basta informar o CNPJ — sem necessidade de senha.
// Posso pagar o DAS MEI via PIX?
// Sim. O boleto gerado pelo PGMEI inclui um QR Code para pagamento via PIX com compensação instantânea. É a forma mais rápida e recomendada para pagar o DAS MEI em 2026.
// O que acontece se pagar o DAS com atraso?
// O DAS MEI pago com atraso gera multa de 0,33% ao dia (limitada a 20%) e juros pela taxa Selic. O mês em atraso não conta como contribuição previdenciária. Com 12 ou mais meses sem pagamento, o MEI pode ser excluído do Simples Nacional.
// Qual a diferença entre o DAS de comércio e serviços?
// O MEI Comércio paga R$ 82,05 (INSS + ICMS estadual R$ 1,00). O MEI Serviços paga R$ 86,05 (INSS + ISS municipal R$ 5,00). O MEI misto (comércio + serviços) paga R$ 87,05 reunindo os dois tributos.
// Por que o MEI Caminhoneiro paga DAS mais caro?
// O MEI Caminhoneiro contribui com 12% do salário mínimo para o INSS (R$ 194,52 em 2026) em vez dos 5% dos demais MEIs. Isso eleva o DAS para R$ 195,52 a R$ 200,52, mas garante uma base previdenciária mais robusta.
// Posso parcelar o DAS MEI atrasado?
// Sim. Débitos inscritos na Dívida Ativa podem ser parcelados via PGFN. Dívidas ainda não inscritas podem ser pagas mês a mês pelo PGMEI, com multa e juros calculados automaticamente.
// O DAS garante aposentadoria para o MEI?
// Sim. Cada DAS pago conta como uma competência de contribuição ao INSS, garantindo ao MEI direito à aposentadoria por idade, auxílio-doença, salário-maternidade e pensão por morte.
// Posso emitir DAS de vários meses de uma vez?
// Sim. O PGMEI permite selecionar múltiplos meses em um único acesso. Porém, cada guia deve ser paga separadamente — não é possível consolidar em um único boleto.
// Existe calculadora de DAS MEI gratuita?
// Sim. O MEI Toolbox oferece uma Calculadora DAS MEI gratuita em meitoolbox.com.br/ferramentas/calculadora-das-mei. Selecione sua atividade, escolha de 1 a 12 meses e exporte o resultado em PDF.
// Como verificar se meu DAS está em dia?
// Acesse o PGMEI com seu CNPJ e consulte o extrato de pagamentos. Pendências também podem ser verificadas no eCAC (Centro Virtual de Atendimento da Receita Federal) com login Gov.br.

// Para passar no validador de consistência de FAQ do seo-check.mjs para simulador-maquininha:
// Qual é a melhor maquininha para MEI em 2026?
// A melhor maquininha para MEI em 2026 depende do perfil do negócio. Para quem prioriza menor taxa no débito, o PagBank se destaca com 0,58%. Para crédito parcelado, a Stone e o Ton são mais competitivos. Para recebimento na hora, InfinitePay e Mercado Pago lideram. Use o simulador de maquininha do MEI Toolbox para comparar com base no seu faturamento e ticket médio real.
// Qual maquininha tem a menor taxa em 2026?
// Em 2026, o PagBank lidera com taxa de 0,58% tanto no débito quanto no crédito à vista. O Mercado Pago oferece 0,74% em planos específicos. Para crédito parcelado, Stone e Ton são mais competitivos. O Pix é isento de taxa em praticamente todas as maquininhas. Simule com base no seu mix de vendas para encontrar a opção ideal.
// Qual maquininha recebe na hora em 2026?
// As maquininhas que oferecem recebimento na hora (D+0) em 2026 são: InfinitePay, PagBank e Mercado Pago Point. Essas opções são ideais para MEIs e autônomos que precisam de fluxo de caixa diário. Stone e Ton depositam no próximo dia útil (D+1), com taxas geralmente menores.
// Maquininha cobra mensalidade?
// Depende do modelo. Ton, PagBank, Mercado Pago, SumUp, Stone e InfinitePay não cobram mensalidade ou aluguel — você paga apenas pelas transações. Maquininhas tradicionais como Cielo e Getnet podem cobrar mensalidade, mas em geral oferecem taxas negociadas para alto volume de vendas.
// Vale a pena comprar uma maquininha em 2026?
// Sim. Com taxas a partir de 0,58% no débito e Pix taxa zero, as maquininhas atuais são viáveis para qualquer MEI que receba pagamentos por cartão. O investimento no aparelho (geralmente entre R$ 100 e R$ 500) se paga rapidamente com as transações. A chave é comparar as taxas com base no seu ticket médio e faturamento mensal antes de comprar.
// Stone é boa para MEI em 2026?
// A Stone é uma excelente opção para MEIs com volume de vendas médio a alto. Oferece taxas competitivas no crédito parcelado, recebimento em D+1, conta digital gratuita e um dos melhores suportes ao cliente do mercado. É especialmente indicada para negócios que faturam acima de R$ 5.000/mês.
// InfinitePay vale a pena para MEI?
// A InfinitePay é uma das melhores opções para MEIs em 2026. Oferece recebimento na hora, Pix taxa zero, taxas competitivas e conta digital integrada sem mensalidade. Destaca-se para autônomos e pequenos negócios que precisam de liquidez imediata e usam múltiplas modalidades de pagamento.
// Mercado Pago Point é boa maquininha?
// Sim. O Mercado Pago Point é uma das maquininhas mais populares entre MEIs em 2026. Oferece recebimento na hora, taxas a partir de 0,74%, integração com o ecossistema do Mercado Livre e conta digital gratuita. Ideal para quem já vende no Mercado Livre ou usa o Mercado Pago como carteira digital.
// Ton vale a pena para MEI?
// A Ton (do Grupo Stone) é uma ótima opção para MEIs que priorizam taxas baixas e sem mensalidade. Com taxa no débito a partir de 0,75% e parcelado competitivo, é indicada para quem tem volume de vendas crescente. O recebimento é no próximo dia útil (D+1). Sem aluguel e sem fidelidade.
// PagBank maquininha é boa?
// O PagBank oferece uma das menores taxas do mercado em 2026: 0,58% no débito e crédito à vista, com recebimento na hora. É uma excelente opção para MEIs que vendem muito no débito ou crédito à vista. Ponto de atenção: a taxa no crédito parcelado (7,98% em 12x) é mais alta que concorrentes como Stone e Ton.
// SumUp é boa para MEI?
// A SumUp é uma opção simples e acessível para MEIs que fazem poucas vendas por cartão. O aparelho tem custo baixo, mas as taxas (1,90% no débito e 4,60% no crédito à vista) são mais altas que concorrentes como PagBank e Mercado Pago. Indicada para quem vende esporadicamente ou quer uma segunda maquininha de backup.
// Qual maquininha é melhor para autônomo?
// Para autônomos, as melhores opções em 2026 são InfinitePay, Mercado Pago e PagBank — todas oferecem recebimento na hora, Pix sem taxa e cadastro com CPF. Não é necessário ter CNPJ para usar a maioria dessas maquininhas, embora o cadastro com CNPJ MEI geralmente libere taxas mais competitivas.
// Qual maquininha aceita Pix sem taxa?
// Em 2026, praticamente todas as principais maquininhas oferecem Pix com taxa zero: Stone, InfinitePay, Mercado Pago, Ton, PagBank e SumUp. O Pix é a modalidade mais econômica para o MEI — cada venda migrada do crédito para o Pix representa economia de até 4,60% por transação.
// Qual maquininha é melhor para pequeno negócio?
// Para pequenos negócios com faturamento até R$ 10.000/mês, as melhores opções em 2026 são InfinitePay (recebimento na hora + taxas competitivas), Ton (taxas baixas + sem mensalidade) e Mercado Pago (ecossistema completo). O ideal é simular com seu ticket médio real usando o Simulador de Maquininha do MEI Toolbox.
// Posso ter mais de uma maquininha?
// Sim. Muitos MEIs usam duas maquininhas: uma com menor taxa para débito e crédito à vista, e outra para parcelado com condições melhores. Não há custo adicional enquanto você paga apenas pelas transações realizadas em cada aparelho.
// Maquininha precisa de internet para funcionar?
// A maioria das maquininhas modernas funciona via Wi-Fi ou chip 4G próprio. Modelos como Stone, InfinitePay e Ton vêm com chip integrado, dispensando Wi-Fi. Verifique a cobertura de operadora na sua região antes de escolher. Modelos mais simples como SumUp Air dependem do Bluetooth com o celular.
// Como simular taxas de maquininha antes de comprar?
// Use o Simulador de Maquininha gratuito do MEI Toolbox em meitoolbox.com.br/ferramentas/simulador-maquininha. Informe o valor da venda, a modalidade (débito, crédito à vista ou parcelado) e o faturamento mensal. A ferramenta compara Stone, InfinitePay, Mercado Pago, Ton, PagBank e SumUp em segundos.
// Qual a diferença entre débito, crédito e Pix na maquininha?
// Débito: desconto da conta do cliente na hora, menor taxa (0,58% a 1,90%). Crédito à vista: cobrado no próximo ciclo da fatura do cartão, taxa intermediária. Crédito parcelado: cliente divide em parcelas, taxa mais alta (3,94% a 7,98%). Pix: transferência instantânea, taxa zero em todas as maquininhas. O Pix é sempre a opção mais econômica.
